/**
 * responseDepthResolver.js
 *
 * Resolves appropriate response depth for a given pastoral situation.
 * Depth is orthogonal to verbosity mode — it captures the "how much"
 * dimension of pastoral wisdom, informed by emotional load and complexity.
 *
 * Levels:
 *   micro      — one phrase; acute grief, extreme exhaustion
 *   short      — 1-2 sentences; quick factual exchanges
 *   medium     — 2-4 sentences; default pastoral
 *   deep       — 4-6 sentences; analysis, spiritual complexity
 *   reflective — contemplative, rich; sermon, deep spiritual questions
 */

export const DEPTH_LEVELS = ["micro", "short", "medium", "deep", "reflective"];

/**
 * @param {{
 *   intent:             string,
 *   emotion:            object|null,
 *   mood:               string,
 *   riskLevel:          string,
 *   messageLength:      number,
 *   conversationLength: number,
 *   ministryLoad:       string,
 * }} params
 * @returns {"micro"|"short"|"medium"|"deep"|"reflective"}
 */
export function resolveResponseDepth({
  intent,
  emotion,
  mood,
  riskLevel          = "none",
  messageLength      = 50,
  conversationLength = 0,
  ministryLoad       = "low",
}) {
  // Acute grief or severe exhaustion → minimal presence
  if (emotion?.dominantEmotion === "grief" && emotion?.intensity !== "low") return "micro";
  if (emotion?.dominantEmotion === "exhaustion" && emotion?.intensity === "high") return "micro";

  // Critical ethical risk → moderate (don't overwhelm, don't dismiss)
  if (riskLevel === "critical") return "short";

  // Very short message with no emotional load → quick exchange
  if (
    messageLength < 20 &&
    !emotion?.detected &&
    intent !== "sermon" &&
    intent !== "reflection"
  ) {
    return "short";
  }

  // Sermon, spiritual meditation → richest depth
  if (intent === "sermon" || intent === "reflection") return "reflective";

  // Analysis queries → deep (precise, structured)
  if (intent === "analysis" && !emotion?.detected) return "deep";

  // Spiritual confusion needs thoughtful, non-rushed response
  if (emotion?.dominantEmotion === "spiritual_confusion") return "deep";

  // Overloaded pastor asking quick operational question
  if (ministryLoad === "critical" && (intent === "planning" || intent === "general")) return "short";

  // Long conversation — don't add bulk late in exchange
  if (conversationLength >= 8 && intent !== "sermon" && intent !== "reflection") return "medium";

  return "medium";
}
